import { SagaIterator } from 'redux-saga';
import { call } from 'redux-saga/effects';
import { Messages, Formatters } from '../../codegen/Localize';
import { DeleteFlowDraftReduxAction, FlowForDeleteDraft } from '../actions/DeleteFlowDraftReduxAction';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Flows } from '../../ts/resources/Flows';
import { Set } from '../../ts/models/Set';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { IErrorItem } from '../../ts/models/MultipleItemResult';
import { IErrorDetail, actionSuccessTranslation, actionErrorTranslation } from '../../ts/services/ContentActionNotification';
import { ActionHelpers } from '../../ts/services/ActionHelpers';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { reloadSpace } from '../../app/sagas/spaces/content/RefreshContentSagas';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { getFlowDraftId } from '../../drafts/utils/DraftUtils';


export type DeleteFlowDraftsDialogActionParams = {
  readonly flows: ReadonlyArray<FlowForDeleteDraft>;
}

export function* handleDeleteFlowDraft(action: DeleteFlowDraftReduxAction): SagaIterator {
  if (action.flows && action.flows.length && action.flows[0].contentType === 'flow') {
    try {
      const flowsWithDrafts = action.flows.filter((flow) => {
        return getFlowDraftId(flow);
      });
      const noFlowDraftsSelected = flowsWithDrafts.length < 1;

      if (noFlowDraftsSelected) {
        const toaster = ToasterService.instance;
        toaster.pop({
          type: ToastType.Error,
          text: Messages.deleteFlowDraft_actionNoDraftsSelected()
        });
        return;
      }
      yield call(openDialog, flowsWithDrafts);
      yield call(reloadSpace);
    } catch(e) {
      // do nothing
    }
  }
}

function errorDetailFor(errorItem: IErrorItem<FlowForDeleteDraft>): IErrorDetail {
  const itemName = errorItem.item.name;
  let errorMessage: string;

  switch (errorItem.error.code) {
    // TODO update when BE adds appropriate error codes
    default:
      errorMessage = Messages.errorUnknown();
  }

  return { itemName, errorMessage };
}

function notifyResult(flows: Set<FlowForDeleteDraft>, result: IResult) {

  ActionHelpers.notifyStandardError(
    flows,
    result,
    {
      success: actionSuccessTranslation(Messages.deleteFlowDraft_actionSuccess),
      error: actionErrorTranslation(Messages.deleteFlowDraft_actionFailed),
    },
    (errorItem: IErrorItem<FlowForDeleteDraft>) => errorDetailFor(errorItem)
  );
}

export async function makeApiCall(props: DeleteFlowDraftsDialogActionParams): Promise<IResult> {
  const flows = ServiceLocator.get<Flows>('Flows');

  const draftIds: string[] = props.flows.map((flow) => {
    return getFlowDraftId(flow);
  });

  const result = await flows.deleteFlowDrafts(draftIds);

  return result;
}

function openDialog(flows: ReadonlyArray<FlowForDeleteDraft>) {
  const modalInstance = openReactConfirmationDialog<DeleteFlowDraftsDialogActionParams, IResult>({
    title: Messages.deleteFlowDraftDialogTitle(),
    message: Messages.confirmDeleteFlowDrafts({ COUNT: flows.length, COUNT_LOC: Formatters.formatCount(flows.length) }),
    actionName: Messages.confirmDeleteButton({ITEM_COUNT: flows.length, ITEM_COUNT_LOC: Formatters.formatCount(flows.length)}),
    action: makeApiCall,
    paramsForAction: { flows },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  modalInstance.result.then((result: IResult) => {
    notifyResult(Set.fromIdItemArray(flows), result);
  });

  return modalInstance.result;
}
